import { useMemo, useCallback, useState, useEffect } from 'react'

import { BankNode, useAllBanksQuery } from '@/apollo/graphql'
import { IOption, IPageInfo, Maybe } from '@/types'
import { getFetchMoreStatus } from '@/utils'

interface IUseSelect {
  loading: boolean
  initialBank: Maybe<Partial<BankNode>>
}

const pageSize = 50

const initialPageInfo: IPageInfo = {
  hasNextPage: false,
  offset: 0,
  fetchMoreStatus: 'loading',
}

export const useBankSelect = ({ loading, initialBank }: IUseSelect) => {
  const [banks, setBanks] = useState<Partial<BankNode>[]>([])
  const [banksPageInfo, setBanksPageInfo] = useState<IPageInfo>(initialPageInfo)

  const { loading: loadingBanks, networkStatus: banksNetworkStatus } =
    useAllBanksQuery({
      fetchPolicy: 'cache-first',
      skip: loading,
      variables: {
        offset: banksPageInfo?.offset || 0,
        first: pageSize,
      },
      onCompleted: res => {
        if (res.allBanks?.edges) {
          const _banks: Partial<BankNode>[] = []
          res.allBanks.edges.forEach(e => {
            if (e?.node) _banks.push(e.node)
          })
          setBanks(prev => [...prev, ..._banks])
          setBanksPageInfo(prev => ({
            ...prev,
            hasNextPage: res.allBanks?.pageInfo.hasNextPage,
          }))
        }
      },
    })

  const bankOptions: IOption<string>[] = useMemo(() => {
    let exists = false

    const opts = banks.map(s => {
      if (!exists && initialBank?.id === s.id) {
        exists = true
      }
      return {
        label: s.name || '',
        value: s.id || '',
      }
    })

    if (!exists && initialBank?.id) {
      opts.unshift({
        label: initialBank?.name || '',
        value: initialBank?.id || '',
      })
    }
    return opts
  }, [banks, initialBank?.id, initialBank?.name])

  useEffect(() => {
    setBanksPageInfo(prev => ({
      ...prev,
      fetchMoreStatus: getFetchMoreStatus({
        networkStatus: banksNetworkStatus,
        hasNextPage: prev?.hasNextPage,
        withForce: true,
      }),
    }))
  }, [banksNetworkStatus])

  const onFetchMore = useCallback(() => {
    setBanksPageInfo(prev => ({ ...prev, offset: banks.length }))
  }, [banks.length])

  return {
    loadingBanks,
    bankOptions,
    banksPageInfo,
    onFetchMore,
  }
}
